import { inject } from '@angular/core';
import { combineLatest, filter, map, Observable, of, switchMap, take } from 'rxjs';
import { CanActivateChildFn, CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthService } from '../auth/services/auth.service';
import {
  LOGIN_URL,
  NLS_SEARCH_URL,
  PLANS_PAGE_URL,
  PRICING_PAGE_URL,
  REPORTS_URL,
  SETTINGS_BASIC_INFO_URL,
} from '../../app-routing-paths';
import { Store } from '@ngrx/store';
import {
  getIsUserSubscriptionTypeLoaded,
  getUserSubscriptionType,
} from '@app/collections/users/users.selectors';
import { SubStatus, SubType } from '@app/shared/models/sub-type';
import { FastrFeatureAccessService } from '@app/shared/services/fastr-feature-access.service';
import { FeatureAccessConfig } from '@app/shared/config/feature-access.config';

export const authGuard = (): Observable<boolean | unknown> => {
  const authorizationService = inject(AuthService);
  const router = inject(Router);

  return authorizationService.isUserAuthorized$.pipe(
    map((isLoggedIn) => {
      if (isLoggedIn) {
        return true;
      } else {
        return router.parseUrl(LOGIN_URL);
      }
    }),
  );
};

export const loginGuard = (): Observable<boolean | unknown> => {
  const authorizationService = inject(AuthService);
  const router = inject(Router);

  return authorizationService.isUserAuthorized$.pipe(
    map((isLoggedIn) => {
      if (isLoggedIn) {
        return router.parseUrl('/');
      } else {
        return false;
      }
    }),
  );
};

export const canActivateReports = (): Observable<boolean | UrlTree> => {
  const router = inject(Router);
  return inject(FastrFeatureAccessService)
    .getAccessibleReports()
    .pipe(
      take(1),
      map((reports) => reports.length > 0 || router.createUrlTree([SETTINGS_BASIC_INFO_URL])),
    );
};

export const canActivateReport: CanActivateFn = (route) => {
  const router = inject(Router);
  return inject(FastrFeatureAccessService)
    .hasFeatureAccess(route.data['featureAccess'])
    .pipe(
      take(1),
      map((hasAccess) => hasAccess || router.createUrlTree([REPORTS_URL])),
    );
};

export const reportsEntryGuard = (): Observable<boolean | UrlTree> => {
  const router = inject(Router);
  return inject(FastrFeatureAccessService)
    .getAccessibleReports()
    .pipe(
      take(1),
      map(([firstReport]) => router.createUrlTree([firstReport?.url ?? SETTINGS_BASIC_INFO_URL])),
    );
};

export const canActivateVanguard = (): Observable<boolean | UrlTree> => {
  const router = inject(Router);

  return inject(FastrFeatureAccessService)
    .hasFeatureAccess(FeatureAccessConfig.VANGUARD_RECRUITER)
    .pipe(
      take(1),
      map((hasAccess) => (hasAccess ? true : router.parseUrl('/'))),
    );
};

export const subscriptionGuard: CanActivateChildFn = (route, state) => {
  const store = inject(Store);
  const router = inject(Router);
  const featureAccessService = inject(FastrFeatureAccessService);
  if (state.url.includes(REPORTS_URL)) {
    return true;
  }
  return combineLatest([
    featureAccessService.hasFeatureAccess(FeatureAccessConfig.VANGUARD_RECRUITER),
    featureAccessService.hasFeatureAccess(FeatureAccessConfig.VANGUARD),
  ]).pipe(
    take(1),
    switchMap(([isVanguardOn, isVanguardRecruiter]) => {
      if (isVanguardOn || isVanguardRecruiter) {
        return of(true);
      }
      return featureAccessService.hasFeatureAccess(FeatureAccessConfig.PRO).pipe(
        take(1),
        switchMap((isPro) =>
          isPro
            ? validateSubscription(store, router, state.url)
            : allowOnlyBasicAccess(featureAccessService, router, state.url),
        ),
      );
    }),
  );
};

const allowOnlyBasicAccess = (
  featureAccessService: FastrFeatureAccessService,
  router: Router,
  url: string,
): Observable<boolean | UrlTree> => {
  if (url.split('?')[0] === SETTINGS_BASIC_INFO_URL) {
    return of(true);
  }
  return featureAccessService.getAccessibleReports().pipe(
    take(1),
    map((reports) =>
      router.createUrlTree([reports.length ? REPORTS_URL : SETTINGS_BASIC_INFO_URL]),
    ),
  );
};

const validateSubscription = (
  store: Store,
  router: Router,
  url: string,
): Observable<boolean | UrlTree> => {
  return store.select(getIsUserSubscriptionTypeLoaded).pipe(
    filter(Boolean),
    take(1),
    switchMap(() => store.select(getUserSubscriptionType)),
    take(1),
    map((subType) => {
      if (!subType) {
        return router.createUrlTree([REPORTS_URL]);
      }
      const shouldViewBeDisabled =
        subType?.status === SubStatus.CANCELED ||
        (subType?.type === SubType.TRIAL && subType?.isBlocked);
      const isPricing = url.includes(PLANS_PAGE_URL);
      const isChatbotSearchPage = url.includes(NLS_SEARCH_URL);
      if (shouldViewBeDisabled && !isPricing) {
        return router.createUrlTree([PRICING_PAGE_URL + PLANS_PAGE_URL]);
      } else if (subType?.status === SubStatus.UNPAID && !isChatbotSearchPage) {
        return router.createUrlTree([NLS_SEARCH_URL]);
      }

      return true;
    }),
  );
};
